import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog as HeadlessDialog,
  DialogBackdrop,
  DialogPanel,
} from '@headlessui/react'
import { motion, useReducedMotion } from 'framer-motion'
import { Plus } from 'lucide-react'
import { parseNaturalLanguage } from '@/lib/nlParser'
import { useTaskStore } from '@/stores/taskStore'
import { useUIStore } from '@/stores/uiStore'
import { useToastStore } from '@/stores/toastStore'
import { haptic } from '@/lib/haptics'
import { slideUpSheet } from '@/lib/motionConfig'
import { NlHintChips } from './NlHintChips'

/**
 * 모바일 빠른 추가 바텀 시트.
 * 단일 자연어 입력 → parseNaturalLanguage → addTask. 어디서든 1탭 → 입력 → Enter.
 * (BottomNav 연동은 다른 컴포넌트가 담당 — open/onClose 계약만 노출)
 */
export function QuickAddSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const addTask = useTaskStore((s) => s.addTask)
  const activeCategoryId = useUIStore((s) => s.activeCategoryTab)
  const showToast = useToastStore((s) => s.showToast)
  const shouldReduceMotion = useReducedMotion()

  const [text, setText] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // 시트 열릴 때 입력 초기화 + 포커스 (트랜지션 후 수동 포커스로 iOS 키보드 안정화)
  useEffect(() => {
    if (open) {
      setText('')
      setIsSaving(false)
      const id = requestAnimationFrame(() => inputRef.current?.focus())
      return () => cancelAnimationFrame(id)
    }
  }, [open])

  // 자연어 파싱 미리보기 (입력 즉시)
  const parsed = useMemo(() => {
    const raw = text.trim()
    if (!raw) return null
    const result = parseNaturalLanguage(raw)
    if (!result.dueDate && !result.dueTime && !result.priority) return null
    return result
  }, [text])

  const handleSubmit = async () => {
    if (isSaving) return
    const raw = text.trim()
    if (!raw) return
    setIsSaving(true)
    try {
      const { cleanTitle, dueDate, dueTime, priority } = parseNaturalLanguage(raw)
      const now = new Date().toISOString()
      await addTask({
        title: cleanTitle,
        categoryId: activeCategoryId,
        dueDate,
        dueTime,
        status: 'pending',
        priority: priority || 'none',
        isFlagged: false,
        isStarred: false,
        alarm: { enabled: false },
        repeat: { type: 'none', interval: 1 },
        subtasks: [],
        createdAt: now,
        updatedAt: now,
        sortOrder: 0,
      })
      haptic('success')
      showToast('작업을 추가했습니다.', 'success')
      setText('')
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <HeadlessDialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="dialog-backdrop transition data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 flex items-end justify-center">
        <DialogPanel className="w-full">
          <motion.div
            variants={shouldReduceMotion ? undefined : slideUpSheet}
            initial={shouldReduceMotion ? { opacity: 0 } : 'hidden'}
            animate={shouldReduceMotion ? { opacity: 1 } : 'visible'}
            className="w-full bg-surface-primary rounded-t-2xl border-t border-base shadow-e3"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
          >
          {/* Drag handle visual */}
          <div className="flex justify-center pt-2.5 pb-1" aria-hidden="true">
            <div className="w-10 h-1 rounded-full bg-surface-muted" />
          </div>

          <div className="px-4 pt-2 pb-1">
            <h2 className="text-sm font-semibold text-heading mb-2">빠른 추가</h2>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    e.preventDefault()
                    handleSubmit()
                  }
                }}
                placeholder="예: 내일 오후 3시 회의 긴급"
                style={{ fontSize: '16px' }}
                className="flex-1 min-w-0 px-4 py-3 rounded-xl border border-base bg-surface-secondary text-heading placeholder:text-disabled focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!text.trim() || isSaving}
                className="flex-shrink-0 min-w-[44px] min-h-[44px] px-4 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium flex items-center justify-center gap-1 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="저장"
              >
                <Plus className="w-4 h-4" />
                저장
              </button>
            </div>
            <NlHintChips parsed={parsed} className="mt-2 px-1 min-h-[1.5rem]" />
          </div>
          </motion.div>
        </DialogPanel>
      </div>
    </HeadlessDialog>
  )
}
